import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Activity, Heart, Stethoscope, AlertCircle, Watch, ArrowLeft, Shield, Key, Loader2, Users, Building2, FlaskConical, ChevronLeft, Hospital, Briefcase } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSelector';

const RightBranding = ({ t }) => (
  <div className="hidden lg:flex flex-1 bg-gradient-to-br from-orange-500 to-amber-600 items-center justify-center p-12">
    <div className="max-w-lg text-white">
      <div className="mb-8">
        <Activity className="w-16 h-16 mb-4" />
        <h1 className="text-4xl font-bold mb-4">
          {t('health_command_center') || 'Your Health Command Center'}
        </h1>
        <p className="text-xl text-orange-100">
          {t('health_command_desc') || 'AI-powered healthcare platform for doctors, patients, clinics and labs'}
        </p>
      </div>
      <div className="space-y-6">
        <div className="flex items-start space-x-4">
          <div className="bg-white/20 rounded-lg p-3"><Stethoscope className="w-6 h-6" /></div>
          <div>
            <h3 className="font-semibold text-lg mb-1">{t('patient_management_feat') || 'Patient Management'}</h3>
            <p className="text-orange-100">{t('patient_management_desc') || 'Complete health records, appointments & prescriptions'}</p>
          </div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="bg-white/20 rounded-lg p-3"><Activity className="w-6 h-6" /></div>
          <div>
            <h3 className="font-semibold text-lg mb-1">{t('ai_health_analysis') || 'AI Health Analysis'}</h3>
            <p className="text-orange-100">{t('ai_health_analysis_desc') || 'Dual Allopathic + Ayurvedic AI analysis'}</p>
          </div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="bg-white/20 rounded-lg p-3"><Heart className="w-6 h-6" /></div>
          <div>
            <h3 className="font-semibold text-lg mb-1">{t('telemedicine_feat') || 'Telemedicine'}</h3>
            <p className="text-orange-100">{t('telemedicine_desc') || 'Virtual consultations and remote care'}</p>
          </div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="bg-white/20 rounded-lg p-3"><Watch className="w-6 h-6" /></div>
          <div>
            <h3 className="font-semibold text-lg mb-1">{t('wearable_integration') || 'Wearable Integration'}</h3>
            <p className="text-orange-100">{t('wearable_integration_desc') || 'Connect fitness devices for real-time health data'}</p>
          </div>
        </div>
      </div>
      <div className="mt-12 pt-8 border-t border-white/20">
        <p className="text-sm text-orange-100">{t('ai_powered') || 'AI Powered'} &bull; {t('healthcare_solutions') || 'Healthcare Solutions'}</p>
      </div>
    </div>
  </div>
);

const ROLES = [
  {
    id: 'patient',
    label: 'User',
    description: 'Track your health records and wellness',
    icon: Users,
    bgClass: 'bg-emerald-100',
    hoverBg: 'hover:bg-emerald-50',
    iconClass: 'text-emerald-600',
    dashboard: '/dashboard/health',
    emailPlaceholder: 'you@example.com',
  },
  {
    id: 'doctor',
    label: 'Doctor / Healthcare Provider',
    description: 'Manage patients, prescriptions & consultations',
    icon: Stethoscope,
    bgClass: 'bg-blue-100',
    hoverBg: 'hover:bg-blue-50',
    iconClass: 'text-blue-600',
    dashboard: '/dashboard/health',
    emailPlaceholder: 'doctor@example.com',
  },
  {
    id: 'clinic',
    label: 'Clinic / Hospital',
    description: 'Multi-provider facility management',
    icon: Building2,
    bgClass: 'bg-purple-100',
    hoverBg: 'hover:bg-purple-50',
    iconClass: 'text-purple-600',
    dashboard: '/dashboard/health',
    emailPlaceholder: 'clinic@example.com',
  },
  {
    id: 'lab',
    label: 'Diagnostic Lab',
    description: 'Lab reports and test management',
    icon: FlaskConical,
    bgClass: 'bg-orange-100',
    hoverBg: 'hover:bg-orange-50',
    iconClass: 'text-orange-600',
    dashboard: '/dashboard/health',
    emailPlaceholder: 'lab@example.com',
  },
];

const HealthLogin = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [clinicSubType, setClinicSubType] = useState(null); // 'hospital' or 'consultation'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState(null);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleRoleSelect = (roleId) => {
    if (roleId === 'clinic') {
      setSelectedRole('clinic_pending'); // intermediate state
    } else {
      setSelectedRole(roleId);
    }
  };

  const handleClinicSubTypeSelect = (subType) => {
    setClinicSubType(subType);
    setSelectedRole('clinic');
    sessionStorage.setItem('clinic_sub_type', subType);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, mfaRequired ? mfaCode : null);
    
    if (result.success) {
      const role = result.user?.role;
      if (role === 'admin' || role === 'superadmin' || role === 'super_admin') {
        navigate('/admin/dashboard');
      } else {
        const roleConfig = ROLES.find(r => r.id === selectedRole);
        navigate(roleConfig?.dashboard || '/dashboard/health');
      }
    } else if (result.mfaRequired) {
      setMfaRequired(true);
      setMfaUserId(result.userId);
      setLoading(false);
    } else {
      setError(result.error);
      setLoading(false);
      if (mfaRequired) {
        setMfaCode('');
      }
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaCode('');
    setMfaUserId(null);
    setError('');
  };

  const handleBackToRoleSelect = () => {
    setSelectedRole(null);
    setClinicSubType(null);
    setEmail('');
    setPassword('');
    setError('');
    setMfaRequired(false);
    setMfaCode('');
    sessionStorage.removeItem('clinic_sub_type');
  };

  // Step 1: Role Selection
  if (!selectedRole) {
    return (
      <div className="min-h-screen flex">
        {/* Left Side - Role Selection */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white relative">
          <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
            <LanguageSwitcher />
          </div>

          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <img 
                src="/infuse_logo.jpg" 
                alt="Infuse" 
                className="h-16 w-auto object-contain mx-auto"
              />
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                {t('healthtrack_title') || 'HealthTrack Pro'}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Choose how you want to use HealthTrack Pro
              </p>
            </div>

            <div className="space-y-3">
              {ROLES.map((role) => {
                const Icon = role.icon;
                return (
                  <button
                    key={role.id}
                    onClick={() => handleRoleSelect(role.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 ${role.hoverBg} hover:border-gray-300 transition-all text-left group`}
                    data-testid={`role-select-${role.id}`}
                  >
                    <div className={`p-3 rounded-xl ${role.bgClass}`}>
                      <Icon className={`w-6 h-6 ${role.iconClass}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{role.label}</p>
                      <p className="text-sm text-gray-500">{role.description}</p>
                    </div>
                    <ChevronLeft className="w-5 h-5 text-gray-300 rotate-180 group-hover:text-gray-500 transition" />
                  </button>
                );
              })}
            </div>

            <div className="mt-6 text-center space-y-3">
              <p className="text-sm text-gray-600">
                Don&apos;t have an account?{' '}
                <Link to="/register/health" className="font-medium text-orange-600 hover:text-orange-500">
                  Register now
                </Link>
              </p>
              <p className="text-sm text-gray-600">
                Are you a Doctor?{' '}
                <Link to="/doctor-onboarding" className="font-medium text-emerald-600 hover:text-emerald-500">
                  Join our Network
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Branding */}
        <RightBranding t={t} />
      </div>
    );
  }

  // Step 1b: Clinic Sub-Type Selection
  if (selectedRole === 'clinic_pending') {
    return (
      <div className="min-h-screen flex">
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white relative">
          <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
            <button
              onClick={handleBackToRoleSelect}
              className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition font-medium"
              data-testid="back-to-role-select"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Roles</span>
            </button>
            <LanguageSwitcher />
          </div>

          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <img 
                src="/infuse_logo.jpg" 
                alt="Infuse" 
                className="h-16 w-auto object-contain mx-auto"
              />
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                What type of facility?
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Select your facility type so we can tailor your dashboard
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => handleClinicSubTypeSelect('hospital')}
                className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all text-left group"
                data-testid="clinic-subtype-hospital"
              >
                <div className="p-3 rounded-xl bg-purple-100">
                  <Hospital className="w-7 h-7 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-base">Hospital</p>
                  <p className="text-sm text-gray-500 mt-0.5">Full facility with in-patient wards, beds, departments, ICU, emergency & multi-specialty care</p>
                </div>
                <ChevronLeft className="w-5 h-5 text-gray-300 rotate-180 group-hover:text-purple-500 transition" />
              </button>

              <button
                onClick={() => handleClinicSubTypeSelect('consultation')}
                className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left group"
                data-testid="clinic-subtype-consultation"
              >
                <div className="p-3 rounded-xl bg-indigo-100">
                  <Briefcase className="w-7 h-7 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-base">Consultation Clinic</p>
                  <p className="text-sm text-gray-500 mt-0.5">Out-patient only clinic with doctor consultations, appointments & prescriptions</p>
                </div>
                <ChevronLeft className="w-5 h-5 text-gray-300 rotate-180 group-hover:text-indigo-500 transition" />
              </button>
            </div>
          </div>
        </div>

        <RightBranding t={t} />
      </div>
    );
  }

  // Step 2: Login Form (after role is selected)
  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white relative">
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
          <button
            onClick={handleBackToRoleSelect}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition font-medium"
            data-testid="back-to-role-select"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Change Role</span>
          </button>
          <LanguageSwitcher />
        </div>

        <div className="max-w-md w-full space-y-8">
          {/* Logo */}
          <div>
            <div className="flex items-center justify-center">
              <img 
                src="/infuse_logo.jpg" 
                alt="Infuse" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('healthtrack_title') || 'HealthTrack Pro'}<sup className="text-sm align-super">&trade;</sup>
            </h2>
            {/* Show selected role badge */}
            <div className="mt-3 flex justify-center">
              {(() => {
                const roleConfig = ROLES.find(r => r.id === selectedRole);
                const Icon = roleConfig?.icon;
                return (
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${roleConfig?.bgClass} ${roleConfig?.iconClass}`}>
                    {Icon && <Icon className="w-4 h-4" />}
                    Logging in as {roleConfig?.label}
                  </span>
                );
              })()}
            </div>
            <p className="mt-2 text-center text-sm text-gray-600">
              {mfaRequired ? 'Two-Factor Authentication' : (t('healthtrack_subtitle') || 'Sign in to your account')}
            </p>
          </div>

          {/* MFA Step */}
          {mfaRequired ? (
            <div className="mt-8 space-y-6">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Verification Required</h3>
                <p className="text-gray-600 text-sm">
                  Enter the 6-digit code from your authenticator app to continue.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-2">
                    Authentication Code
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="mfa-code"
                      name="mfa-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      autoComplete="one-time-code"
                      autoFocus
                      required
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="appearance-none block w-full pl-12 pr-4 py-4 text-center text-2xl tracking-[0.5em] font-mono border border-orange-200 placeholder-gray-400 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="000000"
                      data-testid="mfa-code-input"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    Or enter a backup code if you don&apos;t have access to your authenticator
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || mfaCode.length < 6}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition"
                  data-testid="mfa-verify-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Login'
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full py-2 text-sm text-gray-600 hover:text-orange-500 transition"
                >
                  &larr; Back to login
                </button>
              </form>
            </div>
          ) : (
            /* Standard Login Form */
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('email') || 'Email'}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                    placeholder={ROLES.find(r => r.id === selectedRole)?.emailPlaceholder || 'email@example.com'}
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('password') || 'Password'}
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                    placeholder={t('enter_password') || 'Enter your password'}
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    {t('remember_me') || 'Remember me'}
                  </label>
                </div>

                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-orange-600 hover:text-amber-500">
                    {t('forgot_password') || 'Forgot password?'}
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('loading') || 'Signing in...'}
                    </>
                  ) : (
                    t('secure_login_health') || 'Sign In'
                  )}
                </button>
              </div>

              <div className="text-center space-y-3">
                {/* Phone Login Option */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                  <div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-gray-500">or</span></div>
                </div>
                <Link
                  to="/login/phone"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all"
                  data-testid="phone-login-link"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  Login with Phone (OTP)
                </Link>

                <p className="text-sm text-gray-600">
                  {t('new_to_healthtrack') || "Don't have an account?"}
                </p>
                <Link 
                  to="/register/health" 
                  className="block w-full py-3 px-4 border-2 border-orange-500 text-orange-600 font-medium rounded-lg hover:bg-orange-50 transition-colors"
                >
                  {t('create_account') || 'Create Account'}
                </Link>
                <p className="text-xs text-gray-500">
                  {t('for_doctors_patients') || 'For Doctors, Patients, Clinics & Labs'}
                </p>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Right Side - Health Messaging */}
      <RightBranding t={t} />
    </div>
  );
};

export default HealthLogin;
